import React, { useState, useEffect } from 'react';
import { Download, Upload, BookOpen, FileText, Share2, Activity, Trash2, Zap, Copy, Link2, Plus, Key, MoreVertical, Edit2, Folder, Loader2 } from 'lucide-react';
import { CONNECTION_TYPES } from '../config/connectionTypes';
import { useOrganization, useClerk } from '@clerk/clerk-react';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';
import './WorkspaceOptionsPanel.css';

function WorkspaceOptionsPanel({
  selectedWorkspace,
  onSync,
  onSave,
  isSaving,
  onInstructions,
  onShare,
  onLogs,
  onFiles,
  onToggleGlossary,
  showGlossary,
  onConnectionClick,
  onAddConnection,
  user,
  onExplore,
  onClone,
  versionStatus,
  onApiKeys
}) {
  const { organization } = useOrganization();
  const clerk = useClerk();
  const [connections, setConnections] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);

  // Load connections when panel opens
  useEffect(() => {
    if (selectedWorkspace?.workspace_id) {
      loadConnections();
    }
  }, [selectedWorkspace?.workspace_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConnections = async () => {
    if (!selectedWorkspace?.workspace_id) return;

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) return;

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/config?workspace_id=${selectedWorkspace.workspace_id}`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        setConnections([]);
        return;
      }

      const data = await response.json();
      const config = data.config;

      const loadedConnections = [];

      // Handle connections format
      if (config.connections) {
        Object.entries(config.connections).forEach(([key, value]) => {
          // New format: type stored in connection_type field (key is GUID)
          // Legacy format: type derived from key (e.g., 'database', 'odoo_1')
          const connTypeId = value.connection_type || key.split('_')[0];
          const connType = CONNECTION_TYPES[connTypeId];

          if (connType) {
            loadedConnections.push({
              id: key,
              type: connTypeId,
              name: value.connection_name || connType.name,
              config: value,
              isLegacy: !value.connection_type // Legacy if no connection_type field
            });
          }
        });
      }

      // Handle legacy database format (top-level database key)
      if (config.database && !config.connections?.database) {
        loadedConnections.push({
          id: 'database',
          type: 'database',
          name: config.database.name || 'Database',
          config: config.database,
          isLegacy: true
        });
      }

      setConnections(loadedConnections);
    } catch (err) {
      console.error('Failed to load connections:', err);
      setConnections([]);
    }
  };
  // Workspace Actions (Save, Clone, Share)
  const workspaceActions = [];

  const hasLocalChanges = versionStatus?.has_local_changes;

  workspaceActions.push({
    id: 'save',
    label: isSaving ? 'Saving...' : 'Save',
    icon: isSaving ? Loader2 : Upload,
    onClick: onSave,
    disabled: isSaving,
    spinning: isSaving,
    indicator: !isSaving && hasLocalChanges
  });

  workspaceActions.push({
    id: 'clone',
    label: 'Clone',
    icon: Copy,
    onClick: onClone
  });

  workspaceActions.push({
    id: 'share',
    label: selectedWorkspace?.is_shared ? 'Unshare' : 'Share',
    icon: Share2,
    onClick: onShare
  });


  // Additional Options (Logs, Delete)
  const additionalOptions = [];

  additionalOptions.push({
    id: 'logs',
    label: 'Logs',
    icon: Activity,
    onClick: onLogs
  });

  if (onFiles) {
    additionalOptions.push({
      id: 'files',
      label: 'Files',
      icon: Folder,
      onClick: onFiles
    });
  }

  additionalOptions.push({
    id: 'sync-remote',
    label: 'Remote',
    icon: Download,
    onClick: onSync
  });

  if (onApiKeys) {
    additionalOptions.push({
      id: 'api-keys',
      label: 'API',
      icon: Key,
      onClick: onApiKeys
    });
  }

  // Reference (Glossary, Instructions)
  const referenceOptions = [];

  referenceOptions.push({
    id: 'instructions',
    label: 'Instructions',
    icon: FileText,
    onClick: onInstructions
  });

  referenceOptions.push({
    id: 'glossary',
    label: 'Glossary',
    icon: BookOpen,
    onClick: onToggleGlossary,
    active: showGlossary
  });

  // Handle clicking outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.connection-menu-container')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const handleConnectionMenuToggle = (connId, event) => {
    event.stopPropagation();
    setOpenMenuId(openMenuId === connId ? null : connId);
  };

  const handleConnectionEdit = (conn) => {
    setOpenMenuId(null);
    onConnectionClick(conn.type, conn.id, conn.config, loadConnections);
  };

  const handleConnectionExplore = (conn) => {
    setOpenMenuId(null);
    if (onExplore) {
      onExplore(conn);
    }
  };

  const handleConnectionDelete = async (conn) => {
    setOpenMenuId(null);

    const connType = CONNECTION_TYPES[conn.type];
    if (!window.confirm(`Delete the "${conn.name}" ${connType?.name || 'connection'}?`)) {
      return;
    }

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) return;

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(
        `${apiBaseUrl}/config/connection/${conn.id}?workspace_id=${selectedWorkspace.workspace_id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete connection');
      }

      // Reload connections list
      loadConnections();
    } catch (err) {
      console.error('Failed to delete connection:', err);
      alert('Failed to delete connection: ' + err.message);
    }
  };

  // Render connections with context menu
  const renderConnectionsList = () => (
    <div className="workspace-options-list">
      {connections.map((conn) => {
        return (
          <div key={conn.id} className="connection-item-row">
            <div className="connection-item-info">
              <Link2 size={15} className="option-icon" />
              <span className="option-label">{conn.name}</span>
            </div>
            <div className="connection-menu-container">
              <button
                className="connection-menu-trigger"
                onClick={(e) => handleConnectionMenuToggle(conn.id, e)}
                title="Connection options"
              >
                <MoreVertical size={16} />
              </button>
              {openMenuId === conn.id && (
                <div className="connection-dropdown-menu">
                  <button
                    className="connection-menu-item"
                    onClick={() => handleConnectionEdit(conn)}
                  >
                    <Edit2 size={14} />
                    <span>Edit</span>
                  </button>
                  <button
                    className="connection-menu-item"
                    onClick={() => handleConnectionExplore(conn)}
                  >
                    <Zap size={14} />
                    <span>Explore</span>
                  </button>
                  <button
                    className="connection-menu-item connection-menu-item-danger"
                    onClick={() => handleConnectionDelete(conn)}
                  >
                    <Trash2 size={14} />
                    <span>Delete</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {/* Add Connection button */}
      {onAddConnection && (
        <button
          className="workspace-option-item add-connection"
          onClick={() => onAddConnection(loadConnections)}
          title="Add Connection"
        >
          <Plus size={15} className="option-icon" />
          <span className="option-label">Add</span>
        </button>
      )}
    </div>
  );

  const renderOptionsList = (options) => (
    <div className="workspace-options-list">
      {options.map((option) => {
        const IconComponent = option.icon;
        const itemClasses = [
          'workspace-option-item',
          option.active ? 'active' : '',
          option.isDanger ? 'danger' : '',
          option.disabled ? 'disabled' : ''
        ].filter(Boolean).join(' ');
        return (
          <button
            key={option.id}
            className={itemClasses}
            onClick={option.onClick}
            disabled={option.disabled}
            title={option.label}
          >
            <IconComponent size={15} className={`option-icon${option.spinning ? ' animate-spin' : ''}`} />
            <span className="option-label">
              {option.label}
              {option.badge && <span className="option-badge">{option.badge}</span>}
              {option.indicator && <span className="option-indicator" />}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="workspace-options-panel">
      {/* Workspace Actions Section */}
      {workspaceActions.length > 0 && (
        <>
          <div className="workspace-options-header">
            <h3>Workspace</h3>
          </div>
          {renderOptionsList(workspaceActions)}
        </>
      )}

      {/* Additional Options Section */}
      {additionalOptions.length > 0 && (
        <>
          <div className="workspace-options-divider" />
          <div className="workspace-options-header">
            <h3>Options</h3>
          </div>
          {renderOptionsList(additionalOptions)}
        </>
      )}

      {/* Reference Section */}
      {referenceOptions.length > 0 && (
        <>
          <div className="workspace-options-divider" />
          <div className="workspace-options-header">
            <h3>Reference</h3>
          </div>
          {renderOptionsList(referenceOptions)}
        </>
      )}

      {/* Connections Section */}
      {(connections.length > 0 || onAddConnection) && (
        <>
          <div className="workspace-options-divider" />
          <div className="workspace-options-header">
            <h3>Connections</h3>
          </div>
          {renderConnectionsList()}
        </>
      )}

    </div>
  );
}

export default WorkspaceOptionsPanel;
